import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import { DropdownOptionValue } from '../../../components/common/Dropdown';
import { FormActions, InputField, TextAreaElement, TimeDropdown } from '../../../components/common/forms';
import { scrollToTop } from '../../../utils';
import { FormPageContainer } from '../FormPageContainer';
import { ContentBlock, Form, GridContainer, RequiredIndicator } from '../layoutComponents';
import { H2 } from '../Text';
import { EventInvitePreview } from './EventInvitePreview';
import { EventTypeDropdown } from './EventTypeDropdown';
import { EventInviteFormState, schema } from './schema';
import { EventForm, EventType } from '../../../types/events';
import { createEvent, publishEvent, updateEvent } from '../../../apis_kenno';
import FormError from '../../../components/common/forms/FormError';
import Submitted from '../Submitted';
import { showNotification } from '../../../context/appActions';
import { NotificationType, useAppDispatch } from '../../../context/appContext';

const EventInviteContent = styled(ContentBlock)`
  gap: 0px;
`;

interface EventInviteProps {
  updateForm?: EventForm;
}

export const EventInvite: React.FC<EventInviteProps> = ({ updateForm }) => {
  const appDispatch = useAppDispatch();
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialId = updateForm ? updateForm.id : null;
  const [formId, setFormId] = useState<string | null>(initialId);
  const [published, setPublished] = useState(false);

  const initialValues = {
    eventType: EventType.CommunityWorkDay,
  };

  const formMethods = useForm<EventInviteFormState>({
    resolver: zodResolver(schema),
    defaultValues: updateForm ? { ...initialValues, ...updateForm.formData } : { ...initialValues },
  });

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  const t = useMessageGetter('forms.eventInvite');
  const tCommon = useMessageGetter('forms');

  const handleSubmitFinal = async (data: EventInviteFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    try {
      let id = formId;
      if (id) {
        await updateEvent(id, data);
      } else {
        id = (await createEvent(data)).id;
      }
      await publishEvent(id);
      handleEmptyForm();
      setPublished(true);
      scrollToTop();
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.publish'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSave = async (data: EventInviteFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    try {
      if (!formId) {
        const eventInvite = await createEvent(data);
        setFormId(eventInvite.id);
      } else {
        await updateEvent(formId, data);
      }
      await showNotification(appDispatch, tCommon('info.saved'), NotificationType.SUCCESS);
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.save'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleEmptyForm = () => {
    formMethods.reset();
  };

  const handleTogglePreview = () => {
    setPreviewOpen(!previewOpen);
    scrollToTop();
  };

  const translationPrefix = 'forms.eventInvite';

  const eventType = formMethods.watch('eventType');

  return (
    <FormPageContainer<EventInviteFormState> formMethods={formMethods}>
      {!previewOpen && <H2>{t('title')}</H2>}
      {published ? (
        <Submitted message={tCommon('info.published')} />
      ) : (
        <Form>
          {previewOpen ? (
            <EventInvitePreview formData={formMethods.getValues()} />
          ) : (
            <>
              <span>
                {t('description.part1')} (<RequiredIndicator>*</RequiredIndicator>) {t('description.part2')}
              </span>
              <EventInviteContent>
                <InputField<EventInviteFormState, DropdownOptionValue>
                  fieldPath="eventType"
                  translationPrefix={translationPrefix}
                  required
                  inputElementRenderer={(props) => <EventTypeDropdown {...props} />}
                />

                <InputField<EventInviteFormState, string>
                  fieldPath="name"
                  overrideLabel={t(eventType === EventType.CommunityMeeting ? 'subject' : 'name')}
                  placeholder={t(eventType === EventType.CommunityMeeting ? 'subject' : 'name')}
                  translationPrefix={translationPrefix}
                  type="text"
                  required
                />

                <GridContainer columnGap="20px" columns={2} mdColumns={1}>
                  <InputField<EventInviteFormState, string>
                    fieldPath="date"
                    translationPrefix={translationPrefix}
                    type="date"
                    required
                  />
                  <InputField<EventInviteFormState, DropdownOptionValue>
                    translationPrefix={translationPrefix}
                    fieldPath={`time`}
                    inputElementRenderer={(props) => <TimeDropdown {...props} />}
                  />
                </GridContainer>
                {eventType === EventType.CommunityMeeting && (
                  <InputField<EventInviteFormState, string>
                    fieldPath="location"
                    placeholder={t('locationPlaceholder')}
                    translationPrefix={translationPrefix}
                    type="text"
                    required
                  />
                )}

                <InputField<EventInviteFormState, string>
                  translationPrefix={translationPrefix}
                  fieldPath="description"
                  overrideLabel={t(eventType === EventType.CommunityMeeting ? 'agenda' : 'eventDescription')}
                  required
                  inputElementRenderer={(props) => (
                    <TextAreaElement
                      placeholder={eventType === EventType.CommunityMeeting ? '' : t('eventDescriptionPlaceholder')}
                      {...props}
                    />
                  )}
                />
                {eventType === EventType.CommunityMeeting && (
                  <>
                    <span style={{ marginBottom: '10px' }}>{t('agendaNote')}</span>
                    <InputField<EventInviteFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="regards"
                      inputElementRenderer={(props) => (
                        <TextAreaElement placeholder={t('regardsPlaceholder')} {...props} />
                      )}
                    />
                  </>
                )}
                {eventType !== EventType.CommunityMeeting && (
                  <InputField<EventInviteFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="contactPerson"
                    placeholder={t('contactPersonPlaceholder')}
                    type="text"
                  />
                )}
                <InputField<EventInviteFormState, string>
                  fieldPath="property"
                  placeholder={t('propertyPlaceholder')}
                  translationPrefix={translationPrefix}
                  type="text"
                  required
                />
              </EventInviteContent>
              {submitFailed && <FormError message={errorMessage} />}
            </>
          )}
          <FormActions
            handleEmptyForm={handleEmptyForm}
            handleTogglePreview={handleTogglePreview}
            handleSave={formMethods.handleSubmit(handleSave)}
            handleSubmitFinal={formMethods.handleSubmit(handleSubmitFinal)}
            previewOpen={previewOpen}
            disabled={isBusy}
          />
        </Form>
      )}
    </FormPageContainer>
  );
};
